import React from "react";
import Header from "../../components/Header";
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getDatabase, ref, get, onValue, set, remove } from "firebase/database";
import { IconContext } from "react-icons";
import { CiLogout, CiSettings } from "react-icons/ci";
import { FiPlus, FiSearch, FiFilter, FiGrid, FiList, FiClock, FiEye, FiLock, FiUnlock, FiArchive } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import rand from "random-key";
import "../App/App.css";
import "./Dashboard.css";

// Create a wrapper component to use hooks
function DashboardPageWrapper(props) {
    const navigate = useNavigate();
    return <DashboardPage {...props} navigate={navigate} />;
}

class DashboardPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            user: null,
            login: false,
            loading: true,
            sandboxes: [],
            filteredSandboxes: [],
            searchTerm: "",
            filterType: "all", // all, public, private
            viewMode: "grid", // grid, list
            stats: {
                total: 0,
                public: 0,
                private: 0,
                archived: 0,
                languages: {}
            },
            recentActivity: []
        };

        this.auth = getAuth();
        this.db = getDatabase();

        this.logout = this.logout.bind(this);
        this.createNewSandbox = this.createNewSandbox.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.handleFilter = this.handleFilter.bind(this);
        this.toggleViewMode = this.toggleViewMode.bind(this);
        this.loadUserSandboxes = this.loadUserSandboxes.bind(this);
    }

    componentDidMount() {
        // Check authentication status
        onAuthStateChanged(this.auth, (user) => {
            if (user) {
                this.setState({
                    user: user,
                    login: true,
                    loading: false
                }, () => {
                    this.loadUserSandboxes();
                });
            } else {
                this.setState({
                    login: false,
                    loading: false
                });
                // Redirect to login if not authenticated
                this.props.navigate("/login");
            }
        });
    }

    async loadUserSandboxes() {
        const userId = this.state.user.uid;
        const sandboxes = [];
        const stats = {
            total: 0,
            public: 0,
            private: 0,
            archived: 0,
            languages: {}
        };

        try {
            // Load private sandboxes
            const privateSnapshot = await get(ref(this.db, `private-sandboxes/${userId}`));
            if (privateSnapshot.exists()) {
                const privateData = privateSnapshot.val();
                Object.keys(privateData).forEach(sessionId => {
                    const sandboxData = privateData[sessionId];
                    const sandbox = {
                        id: sessionId,
                        content: sandboxData.content || '',
                        language: sandboxData.language || 'Unknown',
                        createdon: sandboxData.createdon || '',
                        lastAccessed: sandboxData.lastAccessed || '',
                        isPrivate: true,
                        isArchived: false,
                        type: 'private'
                    };
                    sandboxes.push(sandbox);
                    stats.private++;
                    stats.total++;

                    // Count languages
                    const language = sandbox.language;
                    stats.languages[language] = (stats.languages[language] || 0) + 1;
                });
            }

            // Load public sandboxes (user's public sandboxes)
            const publicSnapshot = await get(ref(this.db, 'code-sessions'));
            if (publicSnapshot.exists()) {
                const publicData = publicSnapshot.val();
                Object.keys(publicData).forEach(sessionId => {
                    const sandboxData = publicData[sessionId];
                    // Only include if user is the owner
                    if (sandboxData.user === userId || sandboxData.originalOwner === userId) {
                        const sandbox = {
                            id: sessionId,
                            content: sandboxData.content || '',
                            language: sandboxData.language || 'Unknown',
                            createdon: sandboxData.createdon || '',
                            lastAccessed: sandboxData.lastAccessed || '',
                            isPrivate: false,
                            isArchived: false,
                            type: 'public'
                        };
                        sandboxes.push(sandbox);
                        stats.public++;
                        stats.total++;

                        // Count languages
                        const language = sandbox.language;
                        stats.languages[language] = (stats.languages[language] || 0) + 1;
                    }
                });
            }

            // Load archived sandboxes
            const archivedSnapshot = await get(ref(this.db, `archived-sandboxes/${userId}`));
            if (archivedSnapshot.exists()) {
                const archivedData = archivedSnapshot.val();
                Object.keys(archivedData).forEach(sessionId => {
                    const sandboxData = archivedData[sessionId];
                    const sandbox = {
                        id: sessionId,
                        content: sandboxData.content || '',
                        language: sandboxData.language || 'Unknown',
                        createdon: sandboxData.createdon || '',
                        lastAccessed: sandboxData.lastAccessed || '',
                        isPrivate: sandboxData.isPrivate || false,
                        isArchived: true,
                        type: 'archived',
                        archivedAt: sandboxData.archivedAt || new Date().toLocaleString()
                    };
                    sandboxes.push(sandbox);
                    stats.archived++;
                    stats.total++;

                    // Count languages
                    const language = sandbox.language;
                    stats.languages[language] = (stats.languages[language] || 0) + 1;
                });
            }

            // Sort by last accessed or created date
            sandboxes.sort((a, b) => {
                const aTime = a.lastAccessed ? new Date(a.lastAccessed) : new Date(a.createdon || 0);
                const bTime = b.lastAccessed ? new Date(b.lastAccessed) : new Date(b.createdon || 0);
                return bTime - aTime;
            });

            this.setState({
                sandboxes,
                filteredSandboxes: sandboxes,
                stats
            });

        } catch (error) {
            console.error('Error loading sandboxes:', error);
        }
    }

    handleSearch(e) {
        const searchTerm = e.target.value.toLowerCase();
        this.setState({ searchTerm }, () => {
            this.filterSandboxes();
        });
    }

    handleFilter(filterType) {
        this.setState({ filterType }, () => {
            this.filterSandboxes();
        });
    }

    filterSandboxes() {
        const { sandboxes, searchTerm, filterType } = this.state;

        let filtered = sandboxes.filter(sandbox => {
            // Search filter
            const matchesSearch = searchTerm === "" ||
                sandbox.content?.toLowerCase().includes(searchTerm) ||
                sandbox.language?.toLowerCase().includes(searchTerm) ||
                sandbox.id?.toLowerCase().includes(searchTerm);

            // Type filter
            const matchesType = filterType === "all" ||
                (filterType === "public" && !sandbox.isPrivate && !sandbox.isArchived) ||
                (filterType === "private" && sandbox.isPrivate && !sandbox.isArchived) ||
                (filterType === "archived" && sandbox.isArchived);

            return matchesSearch && matchesType;
        });

        this.setState({ filteredSandboxes: filtered });
    }

    toggleViewMode() {
        this.setState(prevState => ({
            viewMode: prevState.viewMode === "grid" ? "list" : "grid"
        }));
    }

    async createNewSandbox() {
        const userId = this.state.user.uid;
        const sessionId = rand.generate(10);
        const initialContent = '# Welcome to your new sandbox!\n\n# Start coding here...\n\nprint("Hello, World!")';
        const createdon = new Date().toLocaleString();
        const language = "Python";
        const language_id = 71;
        const mode = "python";

        try {
            // Create as private sandbox for authenticated users
            await set(ref(this.db, `private-sandboxes/${userId}/${sessionId}`), {
                content: initialContent,
                createdon: createdon,
                language: language,
                language_id: language_id,
                mode: mode,
                user: userId,
                lastAccessed: createdon
            });

            // Navigate to the new sandbox
            this.props.navigate(`/box/${sessionId}`);
        } catch (error) {
            console.error('Error creating new sandbox:', error);
        }
    }

    logout() {
        signOut(this.auth).then(() => {
            this.setState({ login: false });
            this.props.navigate("/");
        }).catch((error) => {
            console.error('Logout error:', error);
        });
    }

    async archiveSandbox(sandbox) {
        const userId = this.state.user.uid;
        const sessionId = sandbox.id;
        const archivedAt = new Date().toLocaleString();

        try {
            // Create archived sandbox data
            const archivedData = {
                ...sandbox,
                archivedAt: archivedAt,
                isArchived: true
            };

            // Add to archived-sandboxes
            await set(ref(this.db, `archived-sandboxes/${userId}/${sessionId}`), archivedData);

            // Remove from original location
            if (sandbox.isPrivate) {
                await remove(ref(this.db, `private-sandboxes/${userId}/${sessionId}`));
            } else {
                await remove(ref(this.db, `code-sessions/${sessionId}`));
            }

            // Reload sandboxes
            await this.loadUserSandboxes();
        } catch (error) {
            console.error('Error archiving sandbox:', error);
        }
    }

    async unarchiveSandbox(sandbox) {
        const userId = this.state.user.uid;
        const sessionId = sandbox.id;

        try {
            // Create unarchived sandbox data
            const unarchivedData = {
                content: sandbox.content,
                language: sandbox.language,
                createdon: sandbox.createdon,
                lastAccessed: sandbox.lastAccessed,
                user: userId
            };

            // Add back to original location
            if (sandbox.isPrivate) {
                await set(ref(this.db, `private-sandboxes/${userId}/${sessionId}`), unarchivedData);
            } else {
                await set(ref(this.db, `code-sessions/${sessionId}`), unarchivedData);
            }

            // Remove from archived-sandboxes
            await remove(ref(this.db, `archived-sandboxes/${userId}/${sessionId}`));

            // Reload sandboxes
            await this.loadUserSandboxes();
        } catch (error) {
            console.error('Error unarchiving sandbox:', error);
        }
    }

    renderSandboxCard(sandbox) {
        const { darkMode } = this.props;
        const preview = sandbox.content ?
            sandbox.content.substring(0, 100) + (sandbox.content.length > 100 ? '...' : '') :
            'No content';

        // Handle timestamp properly - ensure it's a string
        let timestamp = 'Unknown';
        if (sandbox.lastAccessed) {
            timestamp = typeof sandbox.lastAccessed === 'object' ?
                sandbox.lastAccessed.lastAccessed || 'Unknown' :
                sandbox.lastAccessed;
        } else if (sandbox.createdon) {
            timestamp = sandbox.createdon;
        }

        const language = sandbox.language || 'Unknown';

        return (
            <div key={sandbox.id} className={`sandbox-card ${darkMode ? 'sandbox-card-dark' : ''} ${sandbox.isArchived ? 'sandbox-archived' : ''}`}>
                <div className="sandbox-card-header">
                    <div className="sandbox-privacy">
                        {sandbox.isPrivate ? <FiLock className="privacy-icon" /> : <FiUnlock className="privacy-icon" />}
                        <span className="privacy-text">{sandbox.isPrivate ? 'Private' : 'Public'}</span>
                        {sandbox.isArchived && <span className="archive-badge">Archived</span>}
                    </div>
                    <div className={`sandbox-language ${language === 'Unknown' ? 'language-unknown' : ''}`}>{language}</div>
                </div>

                <div className="sandbox-content">
                    <h3 className="sandbox-title">
                        <Link to={`/box/${sandbox.id}`} className="sandbox-link">
                            interviewsandbox.com/box/{sandbox.id}
                        </Link>
                    </h3>
                    <p className="sandbox-preview">{preview}</p>
                </div>

                <div className="sandbox-footer">
                    <div className="sandbox-timestamp">
                        <FiClock className="timestamp-icon" />
                        <span>{timestamp}</span>
                    </div>
                    <div className="sandbox-actions">
                        <button
                            className={`archive-btn ${sandbox.isArchived ? 'unarchive' : ''}`}
                            onClick={() => sandbox.isArchived ? this.unarchiveSandbox(sandbox) : this.archiveSandbox(sandbox)}
                            title={sandbox.isArchived ? 'Unarchive' : 'Archive'}
                        >
                            <FiArchive />
                            {sandbox.isArchived ? 'Unarchive' : 'Archive'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderSandboxList(sandbox) {
        const { darkMode } = this.props;
        const preview = sandbox.content ?
            sandbox.content.substring(0, 150) + (sandbox.content.length > 150 ? '...' : '') :
            'No content';

        // Handle timestamp properly - ensure it's a string
        let timestamp = 'Unknown';
        if (sandbox.lastAccessed) {
            timestamp = typeof sandbox.lastAccessed === 'object' ?
                sandbox.lastAccessed.lastAccessed || 'Unknown' :
                sandbox.lastAccessed;
        } else if (sandbox.createdon) {
            timestamp = sandbox.createdon;
        }

        const language = sandbox.language || 'Unknown';

        return (
            <div key={sandbox.id} className={`sandbox-list-item ${darkMode ? 'sandbox-list-item-dark' : ''} ${sandbox.isArchived ? 'sandbox-archived' : ''}`}>
                <div className="list-item-privacy">
                    {sandbox.isPrivate ? <FiLock className="privacy-icon" /> : <FiUnlock className="privacy-icon" />}
                    {sandbox.isArchived && <span className="archive-badge">Archived</span>}
                </div>

                <div className="list-item-content">
                    <div className="list-item-header">
                        <Link to={`/box/${sandbox.id}`} className="list-item-title">
                            interviewsandbox.com/box/{sandbox.id}
                        </Link>
                        <div className="list-item-meta">
                            <span className={`list-item-language ${language === 'Unknown' ? 'language-unknown' : ''}`}>{language}</span>
                            <span className="list-item-timestamp">
                                <FiClock className="timestamp-icon" />
                                {timestamp}
                            </span>
                        </div>
                    </div>
                    <p className="list-item-preview">{preview}</p>
                </div>

                <div className="list-item-actions">
                    <button
                        className={`archive-btn ${sandbox.isArchived ? 'unarchive' : ''}`}
                        onClick={() => sandbox.isArchived ? this.unarchiveSandbox(sandbox) : this.archiveSandbox(sandbox)}
                        title={sandbox.isArchived ? 'Unarchive' : 'Archive'}
                    >
                        <FiArchive />
                        {sandbox.isArchived ? 'Unarchive' : 'Archive'}
                    </button>
                </div>
            </div>
        );
    }

    renderStats() {
        const { stats, darkMode } = this.state;
        const topLanguages = Object.entries(stats.languages)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3);

        return (
            <div className={`stats-container ${darkMode ? 'stats-container-dark' : ''}`}>
                <div className="stats-grid">
                    <div className="stat-card">
                        <h3>{stats.total}</h3>
                        <p>Total Sandboxes</p>
                    </div>
                    <div className="stat-card">
                        <h3>{stats.public}</h3>
                        <p>Public</p>
                    </div>
                    <div className="stat-card">
                        <h3>{stats.private}</h3>
                        <p>Private</p>
                    </div>
                    <div className="stat-card">
                        <h3>{stats.archived}</h3>
                        <p>Archived</p>
                    </div>
                </div>

                {topLanguages.length > 0 && (
                    <div className="top-languages">
                        <h4>Top Languages</h4>
                        <div className="language-list">
                            {topLanguages.map(([language, count]) => (
                                <div key={language} className="language-item">
                                    <span className="language-name">{language}</span>
                                    <span className="language-count">{count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { darkMode } = this.props;
        const {
            login,
            loading,
            filteredSandboxes,
            searchTerm,
            filterType,
            viewMode,
            stats
        } = this.state;

        if (loading) {
            return (
                <div className={`dashboard-loading ${darkMode ? 'dashboard-loading-dark' : ''}`}>
                    <div className="loading-spinner">Loading...</div>
                </div>
            );
        }

        if (!login) {
            return null; // Will redirect to login
        }

        return (
            <div className={`dashboard-wrapper ${darkMode ? 'dashboard-dark' : ''}`}>
                <Header darkMode={darkMode} />

                {/* Login Button - Same position as other pages */}
                <div
                    className="loginBtn"
                    title="logout"
                    onClick={this.logout}
                >
                    <IconContext.Provider value={{
                        size: "1.35em",
                        style: {
                            paddingRight: "0.2rem",
                            marginTop: "0.1rem",
                            position: "fixed",
                            top: "11px",
                            right: "1%"
                        }
                    }}>
                        <CiLogout />
                    </IconContext.Provider>
                </div>

                <div className="dashboard">
                    {/* Stats Section */}
                    {this.renderStats()}

                    {/* Controls Section */}
                    <div className="dashboard-controls">
                        <div className="controls-left">
                            <div className="search-container">
                                <FiSearch className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search sandboxes..."
                                    value={searchTerm}
                                    onChange={this.handleSearch}
                                    className="search-input"
                                />
                            </div>

                            <div className="filter-buttons">
                                <button
                                    className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                                    onClick={() => this.handleFilter('all')}
                                >
                                    All ({stats.total})
                                </button>
                                <button
                                    className={`filter-btn ${filterType === 'public' ? 'active' : ''}`}
                                    onClick={() => this.handleFilter('public')}
                                >
                                    Public ({stats.public})
                                </button>
                                <button
                                    className={`filter-btn ${filterType === 'private' ? 'active' : ''}`}
                                    onClick={() => this.handleFilter('private')}
                                >
                                    Private ({stats.private})
                                </button>
                                <button
                                    className={`filter-btn ${filterType === 'archived' ? 'active' : ''}`}
                                    onClick={() => this.handleFilter('archived')}
                                >
                                    Archived ({stats.archived})
                                </button>
                            </div>
                        </div>

                        <div className="controls-right">
                            <button
                                className="view-toggle-btn"
                                onClick={this.toggleViewMode}
                                title={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}
                            >
                                <IconContext.Provider value={{ size: "1.2em" }}>
                                    {viewMode === 'grid' ? <FiList /> : <FiGrid />}
                                </IconContext.Provider>
                            </button>

                            <button className="create-btn" onClick={this.createNewSandbox}>
                                <FiPlus className="create-icon" />
                                New Sandbox
                            </button>
                        </div>
                    </div>

                    {/* Sandboxes Section */}
                    <div className="sandboxes-section">
                        {filteredSandboxes.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">📝</div>
                                <h3>No sandboxes found</h3>
                                <p>
                                    {searchTerm || filterType !== 'all'
                                        ? 'Try adjusting your search or filters'
                                        : 'Create your first sandbox to get started!'
                                    }
                                </p>
                                {!searchTerm && filterType === 'all' && (
                                    <button className="create-btn" onClick={this.createNewSandbox}>
                                        <FiPlus className="create-icon" />
                                        Create Sandbox
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className={`sandboxes-container ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}>
                                {filteredSandboxes.map(sandbox =>
                                    viewMode === 'grid'
                                        ? this.renderSandboxCard(sandbox)
                                        : this.renderSandboxList(sandbox)
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default DashboardPageWrapper; 